/* eslint-disable max-statements */
import { hashKey, useQuery } from '@tanstack/react-query';
import { isEqual } from 'lodash';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { SortDirection, Template, TemplatesCriteria, TemplateType } from '@e-origin/shared';

import { showErrorToast } from '@hooks/showErrorToast';
import { request } from '@utils';

interface FetchTemplatesResponse {
  list: Template[];
  total: number;
}

export const defaultCriteria: TemplatesCriteria = {
  filters: { searchQuery: '', type: TemplateType.HIGH_VALUE_H1 },
  pagination: { page: 1, size: 10 },
  sorting: { field: 'name', direction: SortDirection.ASC },
};

const parseUrlToCriteria = (searchParams: URLSearchParams): TemplatesCriteria => {
  const filters: TemplatesCriteria['filters'] = {};

  filters.type = (searchParams.get('type') || TemplateType.HIGH_VALUE_H1) as TemplateType;
  filters.searchQuery = searchParams.get('searchQuery') || '';
  filters.filterByDeclarants = searchParams.get('declarants')?.split(',')?.filter(Boolean) || [];

  const page = parseInt(searchParams.get('page') || '1', 10);
  const size = parseInt(searchParams.get('size') || '10', 10);

  const sortField = searchParams.get('sortField') || 'name';
  const sortDir = (searchParams.get('sortDir') || SortDirection.ASC) as SortDirection;

  return {
    filters,
    pagination: { page, size },
    sorting: { field: sortField, direction: sortDir },
  };
};

const criteriaToSearchParams = (criteria: TemplatesCriteria): URLSearchParams => {
  const searchParams = new URLSearchParams();

  if (criteria.filters.type) {
    searchParams.set('type', criteria.filters.type);
  }

  if (criteria.filters.searchQuery) {
    searchParams.set('searchQuery', criteria.filters.searchQuery);
  }

  if (criteria.filters.filterByDeclarants) {
    searchParams.set('declarants', criteria.filters.filterByDeclarants.join(','));
  }

  searchParams.set('page', criteria.pagination.page.toString());
  searchParams.set('size', criteria.pagination.size.toString());

  searchParams.set('sortField', criteria.sorting.field);
  searchParams.set('sortDir', criteria.sorting.direction);

  return searchParams;
};

export const getInitialTemplateCriteria = (searchParams: URLSearchParams): TemplatesCriteria => {
  if (searchParams.toString()) {
    return parseUrlToCriteria(searchParams);
  }
  return defaultCriteria;
};

export const useTemplateCriteriaUrlSync = (criteria?: TemplatesCriteria) => {
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (!criteria) return;

    const currentCriteria = parseUrlToCriteria(searchParams);
    if (isEqual(currentCriteria, criteria)) return;

    const newParams = criteriaToSearchParams(criteria);
    setSearchParams(newParams, { replace: true });
  }, [criteria]);
};

export const useAdminTemplates = (criteria: any) => {
  return useQuery({
    queryKey: ['admin-templates', hashKey(criteria)],
    queryFn: async (): Promise<FetchTemplatesResponse> => {
      const { filters, pagination, sorting } = criteria;

      try {
        const {
          data: { list, totalItems },
        } = await request({
          path: `template`,
          method: 'POST',
          authenticate: true,
          dataObject: {
            filters: {
              ...filters,
              type: filters.type === 'other' ? null : filters.type,
            },
            sorting,
            pagination,
          },
        });
        return { list, total: totalItems };
      } catch (error) {
        showErrorToast(error, 'Failed to fetch templates');
      }
    },
  });
};
